/**
 * Spotify Recently Played Service
 * Fetches user's recently played tracks and transforms them to artists
 */

import { Artist } from '../types/MusicPlatform';
import { searchLogger } from '../utils/SearchLogger';

interface SpotifyTrack {
  id: string;
  name: string;
  artists: Array<{
    id: string;
    name: string;
    external_urls: { spotify: string };
    images?: Array<{ url: string; height: number; width: number; }>;
    followers?: { total: number };
    genres?: string[];
  }>;
  album: {
    id: string;
    name: string;
    images: Array<{ url: string; height: number; width: number; }>;
  };
  external_urls: { spotify: string };
}

interface SpotifyRecentlyPlayedResponse {
  items: Array<{
    track: SpotifyTrack;
    played_at: string;
  }>;
  next?: string;
  cursors?: {
    after: string;
    before: string;
  };
}

export class SpotifyRecentlyPlayed {
  private static instance: SpotifyRecentlyPlayed;
  private baseUrl = 'https://api.spotify.com/v1';
  private cache: Artist[] = [];
  private cacheTimestamp: number = 0;
  private readonly cacheTimeout = 5 * 60 * 1000; // 5 minutes

  private constructor() {}

  static getInstance(): SpotifyRecentlyPlayed {
    if (!SpotifyRecentlyPlayed.instance) {
      SpotifyRecentlyPlayed.instance = new SpotifyRecentlyPlayed();
    }
    return SpotifyRecentlyPlayed.instance;
  }

  /**
   * Get recently played artists from Spotify
   */
  async getRecentlyPlayedArtists(): Promise<Artist[]> {
    const searchId = searchLogger.startSearch('recently-played');

    // Check cache first
    if (this.isCacheValid()) {
      searchLogger.cache(searchId, 'hit', 'Recently played cache valid');
      searchLogger.endSearch(searchId, this.cache.length, 'spotify-cache');
      return this.cache;
    }

    searchLogger.cache(searchId, 'miss', 'Recently played cache expired');

    const token = this.getSpotifyToken();
    if (!token) {
      searchLogger.api(searchId, 'spotify', 'error', { error: 'No authentication token available' });
      searchLogger.endSearch(searchId, 0, 'spotify-auth-error');
      return [];
    }

    try {
      searchLogger.api(searchId, 'spotify', 'start');
      const artists = await this.fetchRecentlyPlayedFromAPI(token);
      
      // Update cache
      this.cache = artists;
      this.cacheTimestamp = Date.now();
      searchLogger.cache(searchId, 'set', `Cached ${artists.length} recently played artists`);
      
      searchLogger.api(searchId, 'spotify', 'success', { count: artists.length });
      searchLogger.endSearch(searchId, artists.length, 'spotify');
      return artists;
    } catch (error) {
      searchLogger.api(searchId, 'spotify', 'error', { error: error instanceof Error ? error.message : 'Unknown error' });
      searchLogger.endSearch(searchId, 0, 'spotify-error');
      return [];
    }
  }

  /**
   * Get recently played artists with basic info only (fast version)
   * Returns immediately with basic artist data without waiting for detailed info
   */
  async getRecentlyPlayedArtistsFast(): Promise<Artist[]> {
    const searchId = searchLogger.startSearch('recently-played-fast');

    // Check cache first
    if (this.isCacheValid()) {
      searchLogger.cache(searchId, 'hit', 'Recently played cache valid');
      searchLogger.endSearch(searchId, this.cache.length, 'spotify-cache');
      return this.cache;
    }

    searchLogger.cache(searchId, 'miss', 'Recently played cache expired');

    const token = this.getSpotifyToken();
    if (!token) {
      searchLogger.api(searchId, 'spotify', 'error', { error: 'No authentication token available' });
      searchLogger.endSearch(searchId, 0, 'spotify-auth-error');
      return [];
    }

    try {
      searchLogger.api(searchId, 'spotify', 'start');
      const artists = await this.fetchRecentlyPlayedFromAPIFast(token);
      
      // Update cache with fast results
      this.cache = artists;
      this.cacheTimestamp = Date.now();
      searchLogger.cache(searchId, 'set', `Cached ${artists.length} recently played artists (fast)`);
      
      searchLogger.api(searchId, 'spotify', 'success', { count: artists.length });
      searchLogger.endSearch(searchId, artists.length, 'spotify-fast');
      return artists;
    } catch (error) {
      searchLogger.api(searchId, 'spotify', 'error', { error: error instanceof Error ? error.message : 'Unknown error' });
      searchLogger.endSearch(searchId, 0, 'spotify-error');
      return [];
    }
  }

  /**
   * Clear the cache to force fresh data on next request
   */
  clearCache(): void {
    this.cache = [];
    this.cacheTimestamp = 0;
  }

  /**
   * Fetch recently played artists from Spotify API
   */
  private async fetchRecentlyPlayedFromAPI(token: string): Promise<Artist[]> {
    const response = await fetch(`${this.baseUrl}/me/player/recently-played?limit=50`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Spotify API error: ${response.status} ${response.statusText}`);
    }

    const data: SpotifyRecentlyPlayedResponse = await response.json();
    
    // Extract unique artists from recently played tracks
    const artistMap = new Map<string, any>();
    const uniqueArtistIds = new Set<string>();
    
    // Collect unique artists and their basic info
    for (const item of data.items) {
      for (const spotifyArtist of item.track.artists) {
        if (!artistMap.has(spotifyArtist.id)) {
          artistMap.set(spotifyArtist.id, {
            basic: spotifyArtist,
            album: item.track.album
          });
          uniqueArtistIds.add(spotifyArtist.id);
        }
      }
    }

    // Batch fetch detailed artist info for all unique artists
    const detailedArtistInfo = await this.getBatchArtistInfo(Array.from(uniqueArtistIds), token);
    
    // Transform artists with detailed info
    const artists: Artist[] = [];
    Array.from(artistMap.entries()).forEach(([artistId, artistData]) => {
      const detailedInfo = detailedArtistInfo.get(artistId);
      const artist = this.transformSpotifyArtist(artistData.basic, detailedInfo, artistData.album);
      artists.push(artist);
    });
    
    return artists;
  }

  /**
   * Fetch recently played from API with basic artist info only (fast version)
   */
  private async fetchRecentlyPlayedFromAPIFast(token: string): Promise<Artist[]> {
    const response = await fetch(`${this.baseUrl}/me/player/recently-played?limit=50`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Spotify API error: ${response.status} ${response.statusText}`);
    }

    const data: SpotifyRecentlyPlayedResponse = await response.json();
    
    // Extract unique artists using basic info only (no additional API calls)
    const artistMap = new Map<string, Artist>();
    
    for (const item of data.items) {
      for (const spotifyArtist of item.track.artists) {
        if (!artistMap.has(spotifyArtist.id)) {
          // Use basic artist info without additional API calls
          const artist = this.transformSpotifyArtist(spotifyArtist, null, item.track.album);
          artistMap.set(spotifyArtist.id, artist);
        }
      }
    }

    const artists = Array.from(artistMap.values());
    console.log(`🚀 Fast recently played: returned ${artists.length} artists with basic info only`);
    
    return artists;
  }

  /**
   * Get batch artist information from Spotify API (up to 50 artists)
   */
  private async getBatchArtistInfo(artistIds: string[], token: string): Promise<Map<string, any>> {
    const result = new Map<string, any>();
    
    if (artistIds.length === 0) {
      return result;
    }

    try {
      // Spotify API accepts up to 50 artist IDs in a single request
      const batches = [];
      for (let i = 0; i < artistIds.length; i += 50) {
        batches.push(artistIds.slice(i, i + 50));
      }

      console.log(`🎵 Fetching artist info for ${artistIds.length} artists in ${batches.length} batch(es)`);

      // Process all batches in parallel
      const batchPromises = batches.map(async (batch) => {
        const idsParam = batch.join(',');
        const response = await fetch(`${this.baseUrl}/artists?ids=${idsParam}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          return data.artists || [];
        }
        return [];
      });

      const batchResults = await Promise.all(batchPromises);
      
      // Map results by artist ID
      for (const artists of batchResults) {
        for (const artist of artists) {
          if (artist) { // API can return null for invalid IDs
            result.set(artist.id, artist);
          }
        }
      }

      console.log(`✅ Successfully fetched detailed info for ${result.size} artists`);
      
    } catch (error) {
      console.warn('⚠️ Batch artist info fetch failed (non-blocking):', error);
      // Return empty map - basic artist info will still be available
    }
    
    return result;
  }

  /**
   * Get additional artist information from Spotify API (single artist - legacy method)
   */
  private async getArtistInfo(artistId: string, token: string): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/artists/${artistId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      // Silently continue - additional artist info is optional
    }
    
    return null;
  }


  /**
   * Transform Spotify artist data to unified Artist interface
   */
  private transformSpotifyArtist(
    spotifyArtist: any, 
    artistInfo: any = null, 
    album: any = null
  ): Artist {
    // Use artist info if available, otherwise use data from track
    const info = artistInfo || spotifyArtist;
    
    return {
      name: info.name,
      sortName: info.name,
      verified: true,
      source: 'spotify',
      platform: 'spotify',
      externalUrl: info.external_urls?.spotify,
      followers: info.followers?.total,
      genres: info.genres || [],
      artwork: this.getBestArtworkUrl(info.images, album?.images)
    };
  }

  /**
   * Get the best available artwork URL from artist or album images
   */
  private getBestArtworkUrl(artistImages: any[] = [], albumImages: any[] = []): { url: string; width: number; height: number; } | undefined {
    // Prefer artist images over album images
    const images = artistImages.length > 0 ? artistImages : albumImages;
    
    if (images && images.length > 0) {
      // Find the best size image (prefer around 300x300)
      const preferredImage = images.find(img => img.width >= 300 && img.width <= 640) || images[0];
      
      return {
        url: preferredImage.url,
        width: preferredImage.width,
        height: preferredImage.height
      };
    }
    
    return undefined;
  }

  /**
   * Check if cache is still valid
   */
  private isCacheValid(): boolean {
    return this.cache.length > 0 && (Date.now() - this.cacheTimestamp) < this.cacheTimeout;
  }

  /**
   * Get Spotify access token from localStorage
   */
  private getSpotifyToken(): string | null {
    try {
      const tokenData = localStorage.getItem('spotify_tokens');
      if (!tokenData) return null;
      
      const tokens = JSON.parse(tokenData);
      
      // Check if token is expired
      if (tokens.expires_at <= Date.now()) {
        return null;
      }
      
      return tokens.access_token;
    } catch (error) {
      return null;
    }
  }
}

export default SpotifyRecentlyPlayed;